import { useCallback, useEffect, useRef, useState } from "react";
import {
  isPineScriptWithinLimit,
  PINE_SCRIPT_MAX_CHARS_MESSAGE,
} from "../constants/securityLimits";
import { parseReportStrategyPinePreview } from "../services/reportsApi";
import type { ReportStrategyPineParseResult } from "../types/reportStrategyPine";

export type PineParseSessionState = {
  result: ReportStrategyPineParseResult | null;
  /** Trimmed script text from the last successful parse-preview response. */
  parsedScript: string | null;
  /** True when editor content matches `parsedScript` after a completed parse. */
  isParseCurrent: boolean;
  loading: boolean;
  error: string | null;
  isStale: boolean;
};

export function useManualPineParse(pinescript: string): PineParseSessionState & { parse: () => void } {
  const [result, setResult] = useState<ReportStrategyPineParseResult | null>(null);
  const [parsedScript, setParsedScript] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);
  const pinescriptRef = useRef(pinescript);
  pinescriptRef.current = pinescript;

  const trimmed = pinescript.trim();

  useEffect(() => {
    if (!trimmed) {
      setResult(null);
      setParsedScript(null);
      setError(null);
      setLoading(false);
    }
  }, [trimmed]);

  const parse = useCallback(() => {
    const script = pinescriptRef.current;
    const scriptTrimmed = script.trim();
    if (!scriptTrimmed) {
      setResult(null);
      setParsedScript(null);
      setError(null);
      return;
    }
    if (!isPineScriptWithinLimit(scriptTrimmed)) {
      setError(PINE_SCRIPT_MAX_CHARS_MESSAGE);
      return;
    }

    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const resp = await parseReportStrategyPinePreview(scriptTrimmed);
        if (seq !== seqRef.current) return;
        setResult(resp);
        setParsedScript(scriptTrimmed);
      } catch (e) {
        if (seq !== seqRef.current) return;
        setError(e instanceof Error ? e.message : "Parse failed.");
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    })();
  }, []);

  const isParseCurrent = Boolean(trimmed && parsedScript && trimmed === parsedScript);
  const isStale = Boolean(trimmed && parsedScript && trimmed !== parsedScript);

  return {
    result,
    parsedScript,
    isParseCurrent,
    loading,
    error,
    isStale,
    parse,
  };
}
